import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Play, Pause, Lock, Coins, Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { Loading } from "../components/Layout";
import { Button } from "../components/ui/button";
import CoverImage from "../components/CoverImage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

export default function StoryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refreshCoins } = useAuth();
  const { current, isPlaying, play } = usePlayer();
  const [story, setStory] = useState(null);
  const [unlocking, setUnlocking] = useState("");
  const [reportReason, setReportReason] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/stories/${id}`);
      setStory(data);
    } catch (e) {
      toast.error("Story not found");
      nav("/");
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const onPlay = async (ep) => {
    if (!user) return nav("/auth");
    try {
      await play(ep, { storyTitle: story.title, cover: story.cover_url });
    } catch (e) {
      toast.error(apiError(e, "Could not play"));
    }
  };

  const onUnlock = async (ep) => {
    if (!user) return nav("/auth");
    setUnlocking(ep.id);
    try {
      const { data } = await api.post(`/episodes/${ep.id}/unlock`);
      if (data.status === "unlocked") toast.success(`Unlocked — ${data.coins_spent} coins spent`);
      else toast.info("Already in your library");
      await refreshCoins();
      await load();
    } catch (e) {
      if (e.response?.status === 402) toast.error("Not enough coins — top up your wallet");
      else toast.error(apiError(e));
    } finally {
      setUnlocking("");
    }
  };

  const submitReport = async () => {
    try {
      await api.post("/reports", { target_type: "content", target_id: id, reason: reportReason });
      toast.success("Report submitted. Thank you.");
      setReportOpen(false);
      setReportReason("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!story) return <Loading />;

  return (
    <div className="animate-fade-up">
      <div className="relative h-64 sm:h-80 overflow-hidden">
        {story.cover_url && (
          <CoverImage
            coverUrl={story.cover_url}
            alt=""
            data-testid="story-detail-hero-cover"
            className="h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-28 relative z-10">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="h-40 w-40 rounded-lg overflow-hidden bg-secondary shrink-0 shadow-xl border border-white/10">
            {story.cover_url && (
              <CoverImage
                coverUrl={story.cover_url}
                alt=""
                data-testid="story-detail-cover"
                className="h-full w-full object-cover"
              />
            )}
          </div>
          <div className="flex-1 pt-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="uppercase tracking-[0.15em] text-[10px] bg-primary/80 px-2 py-1 rounded">18+</span>
              <span className="uppercase tracking-[0.15em] text-[10px] text-muted-foreground">{story.category} · {story.language}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">{story.title}</h1>
            <p className="text-muted-foreground mt-1">by {story.creator_name}</p>
            <p className="text-foreground/80 mt-4 leading-relaxed max-w-2xl">{story.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {(story.tags || []).map((t) => (
                <span key={t} className="text-xs text-muted-foreground border border-white/10 rounded-full px-3 py-1">#{t}</span>
              ))}
            </div>
            {user && (
              <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                <DialogTrigger asChild>
                  <button data-testid="report-btn" className="mt-4 text-xs text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center gap-1.5">
                    <Flag size={13} strokeWidth={1.5} /> Report this story
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-card border-white/10">
                  <DialogHeader><DialogTitle className="font-display">Report content</DialogTitle></DialogHeader>
                  <Textarea data-testid="report-reason" value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Describe the issue..." className="bg-black/30 border-white/10" />
                  <DialogFooter>
                    <Button data-testid="report-submit" onClick={submitReport} disabled={!reportReason} className="bg-primary hover:bg-primary/90">Submit report</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="uppercase tracking-[0.2em] text-xs text-muted-foreground mb-4">Episodes</h2>
          <div className="space-y-2" data-testid="episode-list">
            {story.episodes.map((ep) => {
              const active = current?.episode.id === ep.id;
              return (
                <div
                  key={ep.id}
                  data-testid={`episode-${ep.id}`}
                  className={`flex items-center gap-4 rounded-lg border p-4 transition-colors duration-300 ${
                    active ? "border-primary/50 bg-primary/5" : "border-white/5 bg-card/40 hover:bg-card/70"
                  }`}
                >
                  <span className="font-mono text-sm text-muted-foreground w-6">{ep.order}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{ep.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {ep.is_premium ? (
                        <span className="text-gold inline-flex items-center gap-1">
                          <Coins size={12} /> {ep.coin_price} coins
                        </span>
                      ) : (
                        "Free preview"
                      )}
                    </p>
                  </div>
                  {ep.unlocked ? (
                    <Button
                      data-testid={`play-${ep.id}`}
                      onClick={() => onPlay(ep)}
                      size="icon"
                      className="rounded-full bg-primary hover:bg-primary/90 shadow-[0_0_12px_rgba(155,27,48,0.35)]"
                    >
                      {active && isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
                    </Button>
                  ) : (
                    <Button
                      data-testid={`unlock-${ep.id}`}
                      onClick={() => onUnlock(ep)}
                      disabled={unlocking === ep.id}
                      variant="outline"
                      className="border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-300"
                    >
                      {unlocking === ep.id ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} strokeWidth={1.5} />}
                      <span className="ml-2">{ep.coin_price}</span>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
